import { Button } from '@/shared/ui/shadcn/button';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { QUEST_CLINICAL_NOTES_MAX_LENGTH } from '@/entities/labs';

import type { NativeOrderSetLabOrderWizardPrefill } from '../model/labOrderWizardPrefill.js';
import { useNativeLabOrderWizard } from '../model/useNativeLabOrderWizard.js';

import { LabOrderWizardProgress } from './LabOrderWizardProgress.js';
import { NativeLabOrderSelectionStep } from './steps/NativeLabOrderSelectionStep.js';
import { DiseaseCodesStep } from './steps/DiseaseCodesStep.js';
import { NativeLabOrderReviewStep } from './steps/NativeLabOrderReviewStep.js';

export function NativeLabOrderWizard({
  userId,
  prefill,
}: {
  userId: string;
  prefill: NativeOrderSetLabOrderWizardPrefill | null;
}) {
  const wizard = useNativeLabOrderWizard({ userId, prefill });

  return (
    <div className="w-full max-w-3xl">
      {wizard.isEditableMode ? (
        <>
          <LabOrderWizardProgress stepId={wizard.stepId} mode={null} />

          <div className="mt-4">
            <h2 className="text-md font-medium leading-none tracking-tight">
              {wizard.stepMeta.title}
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground">{wizard.stepMeta.subtitle}</p>
          </div>
        </>
      ) : null}

      <div className="mt-6 space-y-6">
        {wizard.errorMessage && (
          <Alert variant="destructive">
            <AlertTitle>Can&rsquo;t continue</AlertTitle>
            <AlertDescription>{wizard.errorMessage}</AlertDescription>
          </Alert>
        )}

        {wizard.stepId === 'select-test' && (
          <NativeLabOrderSelectionStep
            selectedItems={wizard.selectedItems}
            onAddItem={wizard.handleAddItem}
            onRemoveItem={wizard.handleRemoveItem}
          />
        )}

        {wizard.stepId === 'disease-codes' && (
          <DiseaseCodesStep
            aoeSchema={wizard.effectiveAoeSchema}
            isLoadingLabTest={wizard.isResolvingAoe}
            aoeAnswersById={wizard.draft.aoeAnswersById}
            onAoeChange={wizard.handleAoeChange}
            notes={wizard.draft.notes}
            onNotesChange={wizard.handleNotesChange}
            notesMaxLength={QUEST_CLINICAL_NOTES_MAX_LENGTH}
            priority={wizard.draft.priority}
            onPriorityChange={wizard.handlePriorityChange}
            icdCodes={wizard.draft.icdCodes}
            onAddIcdCode={wizard.handleAddIcdCode}
            onRemoveIcdCode={wizard.handleRemoveIcdCode}
          />
        )}

        {wizard.stepId === 'review' && (
          <NativeLabOrderReviewStep
            method={wizard.draft.method}
            selectedItems={wizard.selectedItems}
            answeredAoeQuestionCount={wizard.answeredAoeQuestionCount}
            icdCodes={wizard.draft.icdCodes}
            priority={wizard.draft.priority}
            notes={wizard.draft.notes}
            errorMessage={wizard.submissionState.errorMessage}
            onEditTests={wizard.handleEditTests}
            onEditDetails={wizard.handleEditDetails}
          />
        )}
      </div>

      <div className="mt-6 flex items-center justify-between">
        {!wizard.backHidden && (
          <Button
            type="button"
            variant="secondary"
            radius="pill"
            onClick={wizard.handleBack}
            disabled={wizard.backDisabled}
          >
            Back
          </Button>
        )}

        <div className="flex gap-2">
          {wizard.canStartSeparateManualOrder && (
            <Button
              type="button"
              variant="secondary"
              radius="pill"
              onClick={wizard.handleStartSeparateManualOrder}
            >
              Start separate order
            </Button>
          )}

          <Button
            type="button"
            radius="pill"
            onClick={() => void wizard.handlePrimaryAction()}
            disabled={wizard.primaryDisabled}
          >
            {wizard.primaryLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
